import React from 'react';
import { EventCompletionStatus, AssessmentPdfQuery } from 'src/graphql/_generated';
import { Text, View } from '@react-pdf/renderer';
import { pdfStyles } from '../pdfStyles';
import { ColorSet } from 'src/redux/reducers/theme';
import { formatDate } from 'src/utils/misc';
import { getEventCompletionStatusLabel } from 'src/api/types';
import { ActionsConf } from '../types';
import { PdfLinkifyText } from './PdfLinkifyText';
import _ from 'lodash';

type ActionGql = NonNullable<AssessmentPdfQuery['assessment']>['actions'][number];

interface ActionsTableProps {
  data: ActionGql[];
  colors: ColorSet;
  includesActions: ActionsConf;
}

export const ActionsTable: React.FC<ActionsTableProps> = ({ data, colors, includesActions }) => {
  if (!includesActions.enabled) {
    return null;
  }

  const filteredActions = data.filter((el) => {
    if (includesActions.completionStatus === 'COMPLETED') {
      return el.completionStatus === EventCompletionStatus.Done;
    }
    if (includesActions.completionStatus === 'INCOMPLETED') {
      return el.completionStatus !== EventCompletionStatus.Done;
    }
    return true;
  });

  if (!filteredActions.length) {
    return null;
  }

  const sortedActions = _.orderBy(
    filteredActions,
    [
      (action) => action.completionStatus === EventCompletionStatus.Done,
      (action) => (action.dueDate ? new Date(action.dueDate).getTime() : Infinity),
    ],
    ['asc', 'asc'],
  );

  return (
    <>
      <View style={pdfStyles.spacer16} />
      <Text style={pdfStyles.label}>ACTIONS</Text>
      <View style={pdfStyles.spacer4} />
      <View
        wrap={false}
        break={false}
        style={{
          height: 24,
          border: `1px solid ${colors.cflowerBlue}`,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
          <Text style={pdfStyles.tableHeader}>Owner</Text>
        </View>
        <View style={{ ...pdfStyles.tableCell, flex: 1 }}>
          <Text style={pdfStyles.tableHeader}>Title</Text>
        </View>
        <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
          <Text style={pdfStyles.tableHeader}>Completion Status</Text>
        </View>
        <View style={{ ...pdfStyles.tableCell, flex: 0.5 }}>
          <Text style={pdfStyles.tableHeader}>Due Date</Text>
        </View>
      </View>

      {sortedActions.map((el) => {
        let dueDateColor = colors.prussianBlue;
        if (el.completionStatus === EventCompletionStatus.Done) {
          dueDateColor = colors.cflowerBlue;
        } else if (el.isOverdue) {
          dueDateColor = colors.statusRed;
        }

        return (
          <View
            key={el.id}
            wrap={false}
            break={false}
            style={{
              minHeight: 24,
              border: `1px solid ${colors.cflowerBlue}`,
              display: 'flex',
              flexDirection: 'row',
              marginTop: -1,
            }}
          >
            <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
              <Text style={pdfStyles.tableValue}>{(el.assignedTo?.text || el.assignedTo?.email || '-').split('')}</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 1 }}>
              {el.notes ? (
                <PdfLinkifyText style={pdfStyles.tableValue}>{el.notes}</PdfLinkifyText>
              ) : (
                <Text style={pdfStyles.tableValue}>-</Text>
              )}
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.6 }}>
              <Text style={pdfStyles.tableValue}>{getEventCompletionStatusLabel(el.completionStatus)}</Text>
            </View>
            <View style={{ ...pdfStyles.tableCell, flex: 0.5 }}>
              <Text style={{ ...pdfStyles.tableValue, color: dueDateColor }}>
                {formatDate(el.dueDate, { emptyValue: '-' })}
              </Text>
            </View>
          </View>
        );
      })}
    </>
  );
};
